import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, Clock, Repeat, Square, SquareCheck, X } from 'lucide-react';
import { useState } from 'react';

interface RecurringTaskManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface RecurringTask {
  id: string;
  title: string;
  description?: string;
  recurrenceFrequency: 'daily' | 'monthly' | 'yearly';
  recurrenceNextRun: string;
  recurrenceEndDate?: string;
  dueDate: string;
  status: string;
  createdAt: string;
}

export default function RecurringTaskManagementModal({
  isOpen,
  onClose,
}: RecurringTaskManagementModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [stoppingTaskId, setStoppingTaskId] = useState<string | null>(null);

  // Fetch user's recurring tasks
  const { data: recurringTasks = [], isLoading } = useQuery({
    queryKey: ['/api/recurring-tasks'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/recurring-tasks');
      console.log(
        'Recurring tasks API response:',
        response,
        'Type:',
        typeof response,
        'Is Array:',
        Array.isArray(response)
      );

      // Handle both array response and paginated response
      if (Array.isArray(response)) {
        return response as RecurringTask[];
      } else if (
        response &&
        typeof response === 'object' &&
        'items' in response
      ) {
        return (response as { items: RecurringTask[] }).items;
      } else {
        console.error('Unexpected response format:', response);
        return [];
      }
    },
    enabled: isOpen,
  });

  // Stop recurring task mutation
  const stopRecurringMutation = useMutation({
    mutationFn: async (taskId: string) => {
      await apiRequest('PATCH', `/api/recurring-tasks/${taskId}/stop`);
    },
    onSuccess: () => {
      toast({
        title: 'Recurring Task Stopped',
        description: 'The recurring task will no longer create new instances.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/recurring-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to stop recurring task',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setStoppingTaskId(null);
    },
  });

  const handleStopRecurring = (taskId: string) => {
    setStoppingTaskId(taskId);
    stopRecurringMutation.mutate(taskId);
  };

  const getFrequencyLabel = (frequency: string) => {
    switch (frequency) {
      case 'daily':
        return 'Daily';
      case 'monthly':
        return 'Monthly';
      case 'yearly':
        return 'Yearly';
      default:
        return frequency;
    }
  };

  const getFrequencyIcon = (frequency: string) => {
    switch (frequency) {
      case 'daily':
        return '📅';
      case 'monthly':
        return '📆';
      case 'yearly':
        return '🗓️';
      default:
        return '🔄';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Repeat className="w-5 h-5" />
            Manage Recurring Tasks
          </DialogTitle>
          <DialogDescription>
            Manage your recurring tasks. You can stop any recurring task you
            created to prevent it from generating new instances.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-500">
                Loading recurring tasks...
              </div>
            </div>
          ) : recurringTasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Square className="w-12 h-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Recurring Tasks
              </h3>
              <p className="text-sm text-gray-500">
                You haven't created any recurring tasks yet. Create a task and
                enable recurrence to see it here.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {recurringTasks.map(task => (
                <div
                  key={task.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-medium text-gray-900 truncate">
                          {task.title}
                        </h4>
                        <Badge variant="secondary" className="text-xs">
                          {getFrequencyIcon(task.recurrenceFrequency)}{' '}
                          {getFrequencyLabel(task.recurrenceFrequency)}
                        </Badge>
                      </div>

                      {task.description && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {task.description}
                        </p>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span>
                            Next run: {formatDateTime(task.recurrenceNextRun)}
                          </span>
                        </div>

                        {task.recurrenceEndDate && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span>
                              Ends: {formatDate(task.recurrenceEndDate)}
                            </span>
                          </div>
                        )}

                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>Created: {formatDate(task.createdAt)}</span>
                        </div>

                        <div className="flex items-center gap-1">
                          <SquareCheck className="w-3 h-3" />
                          <span>Status: {task.status}</span>
                        </div>
                      </div>
                    </div>

                    <div className="ml-4 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleStopRecurring(task.id)}
                        disabled={stoppingTaskId === task.id}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                      >
                        {stoppingTaskId === task.id ? (
                          <>
                            <div className="w-3 h-3 border border-red-600 border-t-transparent rounded-full animate-spin mr-1" />
                            Stopping...
                          </>
                        ) : (
                          <>
                            <X className="w-3 h-3 mr-1" />
                            Stop Recurrence
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
